<template>
  <div
    class="track columns is-vcentered is-mobile"
    :class="{active : isPlayingTrack}"
  >
    <div
      v-if="showCover && track.cover_thumbnail"
      class="column cover is-narrow"
    >
      <div
        v-if="!disablePlay"
        class="play"
        :class="{ 'is-pause': isTrackPlaying }"
        @click="togglePlay"
      />
      <img
        :src="track.cover_thumbnail"
        :alt="`Cover art for ${track.display_title} by ${track.artist.name}`"
      >
    </div>
    <div
      v-if="!showCover || !track.cover_thumbnail"
      class="column play-button is-narrow"
    >
      <div
        v-if="!disablePlay"
        class="play"
        :class="{ 'is-pause': isTrackPlaying }"
        @click="togglePlay"
      />
    </div>

    <div class="column">
      <p class="text label-medium track-title">
        <router-link :to="trackRouteTo">
          {{ track.display_title }}
          <span
            v-if="track.explicit == 'Explicit' || track.explicit == true"
            class="tag explicit"
          >Explicit</span>
          <span
            v-if="isNew"
            class="tag new"
          >New</span>
        </router-link>
      </p>
      <p
        v-if="track.featuring_title"
        class="text small track-title is-hidden-mobile"
      >
        <router-link :to="trackRouteTo">
          {{ track.featuring_title }}
        </router-link>
      </p>
      <p
        v-if="showArtist"
        class="text small track-artist"
      >
        <router-link :to="artistRouteTo">
          {{ track.artist.name }}
        </router-link>
      </p>
    </div>
    <div
      v-if="showTags"
      class="column is-narrow instrumental is-hidden-touch"
    >
      <b-tooltip
        v-if="track.instrumental_version !== null && track.instrumental_version !== undefined && track.instrumental_version.length > 0"
        label="Instrumental version available"
        type="is-dark"
        position="is-top"
      >
        <router-link :to="trackRouteTo">
          <span
            class="icon-static small plus-circle"
            style="margin: -4px 0 -4px 0;"
          />
        </router-link>
      </b-tooltip>
      <b-tooltip
        v-if="track.instrumental || track.is_instrumental"
        label="Instrumental"
        type="is-dark"
        position="is-top"
      >
        <span
          class="icon-static small instrumental"
          style="margin: -4px 0 -4px 0;"
        />
      </b-tooltip>
    </div>

    <div
      v-if="showTags"
      class="column is-narrow is-hidden-touch"
    >
      <div class="tags">
        <span
          v-for="genre in track.genres"
          :key="genre"
          class="tag small music-categorisation is-rounded"
        >{{ genre }}</span>
      </div>
    </div>

    <div
      v-if="showWaveform"
      class="column is-narrow is-hidden-mobile waveform"
    >
      <div :id="`waveform-${id}`" />
    </div>

    <div
      v-if="showWaveform"
      class="column is-narrow is-hidden-touch"
      style="width: 50px;"
    >
      <span class="text tiny color-bluegrey3">{{ track.duration|formattedDuration }}</span>
    </div>

    <div
      v-if="showBpm"
      class="column is-narrow is-hidden-touch"
      style="width: 70px;"
    >
      <span
        v-if="track.bpm"
        class="text tiny color-bluegrey3"
      >{{ track.bpm }} BPM</span>
    </div>

    <slot>
      <div class="column is-narrow">
        <div class="buttons are-small">
          <b-tooltip
            label="Add to collection"
            type="is-dark"
            position="is-top"
          >
            <a
              class="icon-button plus-muted"
              :disabled="!isLoggedIn"
              @click="handleAddToCollection"
            />
          </b-tooltip>
          <b-tooltip
            label="Remove from favourites"
            type="is-dark"
            position="is-top"
          >
            <a
              v-show="isFavourite"
              class="icon-button heart-active"
              @click="handleFavouriteDelete"
            />
          </b-tooltip>
          <b-tooltip
            label="Add to favourites"
            type="is-dark"
            position="is-top"
          >
            <a
              v-show="!isFavourite"
              class="icon-button heart-muted"
              @click="handleFavouriteCreate"
            />
          </b-tooltip>
          <b-tooltip
            label="Download preview"
            type="is-dark"
            position="is-top"
          >
            <a
              class="icon-button preview-muted"
              @click="handleShowPreviewModal"
            />
          </b-tooltip>
          <b-tooltip
            label="Similar songs"
            type="is-dark"
            position="is-top"
          >
            <router-link
              class="icon-button link-muted"
              :to="trackRouteTo"
              @click.native="logSimilarSongsAnalytics"
            />
          </b-tooltip>
          <b-tooltip
            :label="!isLicensed ? 'License' : 'You have already licensed this item. Re-license it for another project.'"
            type="is-dark"
            :position="!isLicensed ? 'is-top' : 'is-left'"
          >
            <b-dropdown
              v-if="isLoggedIn"
              class="is-bottom-left"
              aria-role="list"
              @active-change="licenseDropdownChange"
            >
              <a
                slot="trigger"
                class="icon-button"
                :class="{
                  'download-muted': isLicensed,
                  'download': !isLicensed
                }"
              />

              <b-dropdown-item
                aria-role="listitem"
                @click="handleAddToCart"
              >
                <i class="icon-button shopping-cart">
                  <span style="font-style: normal; padding-left: 32px;">Add to cart</span>
                </i>
              </b-dropdown-item>

              <b-dropdown-item
                aria-role="listitem"
                @click="handleShowSubscriptionDownload"
              >
                <i class="icon-button download">
                  <span style="font-style: normal; padding-left: 32px;">Subscription</span>
                </i>
              </b-dropdown-item>
            </b-dropdown>
            <a
              v-if="!isLoggedIn"
              class="icon-button download"
              @click="$store.dispatch('common/startSignupWorkflow', { reason: 'download', track })"
            />
          </b-tooltip>
        </div>
      </div>
    </slot>
  </div>
</template>

<script>
import favouritesClient from '@/api/favouriteTracks.js'
import waveformClient from '@/api/waveforms.js'
import WaveSurfer from 'wavesurfer.js'
import { formatComponentName } from '@/utils/common.js'
import { logPlayTrack, logLicenseActionClick, logSimilarSongsClick } from '@/utils/segment.js'
import { addTrackToCart, showSubscriptionDownload, showPreviewDownload } from '@/utils/trackLicensing.js'

export default {
  name: 'TrackListItem',

  props: {
    track: {
      type: Object,
      required: true
    },
    disablePlay: {
      type: Boolean,
      required: false,
      default: false
    },
    showCover: {
      type: Boolean,
      required: false,
      default: true
    },
    showArtist: {
      type: Boolean,
      required: false,
      default: true
    },
    showWaveform: {
      type: Boolean,
      required: false,
      default: true
    },
    showTags: {
      type: Boolean,
      required: false,
      default: true
    },
    showBpm: {
      type: Boolean,
      required: false,
      default: true
    }
  },

  data () {
    return {
      id: null,
      wavesurfer: null,
      waveData: null,
      creatingFavourite: false,
      deletingFavourite: false
    }
  },

  computed: {
    isLoggedIn: function () {
      return this.$store.getters['auth/isLoggedIn']
    },

    isPlayingTrack: function () {
      return this.$store.getters['player/currentTrack'] ? this.$store.getters['player/currentTrack'].pk === this.track.pk : false
    },

    // Only the loaded track's row reads the progress, so other rows don't re-render
    // on every tick, and a row whose track stops being current drops back to 0.
    playProgress: function () {
      return this.isPlayingTrack ? this.$store.getters['player/playProgress'] : 0
    },

    // isPlayingTrack is true for the loaded track even when it's paused.
    isTrackPlaying: function () {
      return this.isPlayingTrack && this.$store.getters['player/isPlaying']
    },

    trackRouteTo: function () {
      return {
        name: 'track-detail',
        params: {
          slug: this.track.slug,
          artistSlug: this.track.artist.slug,
          trackId: this.track.pk
        }
      }
    },

    artistRouteTo: function () {
      return {
        name: 'artist-detail',
        params: {
          slug: this.track.artist.slug,
          artistId: this.track.artist.pk
        }
      }
    },

    isFavourite: function () {
      if (this.isLoggedIn) {
        if (this.deletingFavourite) {
          return false
        }
        return this.creatingFavourite || this.$store.getters['favouriteTracks/isFavourite'](this.track.pk)
      }
      return false
    },

    isLicensed: function () {
      if (this.isLoggedIn) {
        return this.$store.getters['licenses/isLicensed'](this.track.pk)
      }
      return false
    },

    isNew: function () {
      if (this.track && this.track.created_at) {
        const createdAt = new Date(this.track.created_at)
        const daysThreshold = parseInt(process.env.VUE_APP_NEW_TRACK_THRESHOLD)
        const thresholdDate = new Date()
        thresholdDate.setDate(thresholdDate.getDate() - daysThreshold)
        return createdAt >= thresholdDate
      }
      return false
    },

    artistTrackLabel: function () {
      if (this.track) {
        return `${this.track.artist.name} - ${this.track.display_title}`
      }
      return ''
    },

    componentName: function () {
      return formatComponentName(this)
    }
  },

  watch: {
    playProgress () {
      this.drawPlayProgress()
    }
  },

  created () {
    this.id = this._uid
  },

  mounted () {
    if (this.showWaveform) {
      const waveSelector = `#waveform-${this.id}`
      const audioUri = this.track.streaming_m4a

      this.wavesurfer = WaveSurfer.create({
        container: waveSelector,
        waveColor: '#D0D2D6',
        progressColor: '#FF6C6C', // Same as the player's waveform.
        cursorColor: '#fff',
        barWidth: 1,
        height: 18,
        width: 160,
        interact: false,
        responsive: true
      })
      // Drawing (on load and on resize) resets progress to this instance's own position,
      // which is always 0 as it never plays, so reapply the player's progress.
      this.wavesurfer.on('redraw', this.drawPlayProgress)

      if (typeof this.track.waveform === 'object') {
        this.waveData = this.track.waveform.data
        this.wavesurfer.load(audioUri, this.waveData)
      } else {
        // Assume PK and load data from API.
        const waveformPK = this.track.waveform
        waveformClient.retrieve(waveformPK).then(response => {
          this.waveData = response.data.data
          this.wavesurfer.load(audioUri, this.waveData)
        })
      }
    }
  },

  destroyed () {
    if (this.wavesurfer !== null) {
      this.wavesurfer.destroy()
    }
  },

  methods: {
    handleAddToCollection () {
      if (this.isLoggedIn) {
        this.$store.dispatch(
          'collections/setTrackAddingToCollection',
          this.track
        )
        this.$store.dispatch('collections/showCollectionsModal')
      } else {
        this.$store.dispatch('user/showSignupModal', { reason: 'collection', track: this.track })
      }
    },

    handleFavouriteCreate () {
      if (this.isLoggedIn) {
        this.creatingFavourite = true
        favouritesClient
          .create({ item_pk: this.track.pk })
          .then(response => {
            this.$store.dispatch('favouriteTracks/appendToFavouriteTracks', response.data)
              .then(() => {
                this.creatingFavourite = false
              })
          })
          .catch(() => {
            this.creatingFavourite = false
          })
      } else {
        this.$store.dispatch('user/showSignupModal', { reason: 'favourite', track: this.track })
      }
    },

    handleFavouriteDelete () {
      this.deletingFavourite = true

      const favePk = this.$store.getters['favouriteTracks/getFavouritePkFromTrackPk'](this.track.pk)
      favouritesClient.delete(favePk)
        .then(response => {
          this.$store.dispatch('favouriteTracks/removeFromFavouriteTracks', favePk)
            .then(() => {
              this.deletingFavourite = false
            })
        })
        .catch(() => {
          this.deletingFavourite = false
        })
    },

    handleAddToCart () {
      return addTrackToCart(this, this.track)
    },

    handleShowSubscriptionDownload () {
      showSubscriptionDownload(this, this.track)
    },

    handleShowPreviewModal () {
      showPreviewDownload(this, this.track)
    },

    drawPlayProgress () {
      // Paint straight onto the drawer: seekTo() would count as an interaction and make
      // this peaks-only waveform download the audio.
      if (this.wavesurfer !== null && this.wavesurfer.drawer) {
        this.wavesurfer.drawer.progress(this.playProgress)
      }
    },

    togglePlay () {
      if (this.isTrackPlaying) {
        this.$store.dispatch('player/pause')
        return
      }
      if (this.$store.getters['player/isPlaybackBlocked']) {
        this.$store.dispatch('common/startSignupWorkflow', { reason: 'play', track: this.track })
        return
      }
      if (this.$store.getters['player/isPlaybackSoftBlocked']) {
        this.$store.dispatch('common/startSignupWorkflow', { reason: 'play', track: this.track })
      }
      this.$store.dispatch('player/playTrack', this.track.pk)
      logPlayTrack(this.track.artist.name, this.track.display_title)
    },

    licenseDropdownChange (active) {
      if (!active) return
      logLicenseActionClick()
    },

    logSimilarSongsAnalytics () {
      logSimilarSongsClick(this.$options.name)
    }
  }
}
</script>

<style scoped lang="scss">
@import "../assets/scss/_variables.scss";
  .tag {
    &.explicit {
      font-size: 8px;
      letter-spacing: 0.07em;
      padding: 4px;
      border: 1px solid #f14668 !important; // For some reason using $red doesn't give the correct value!
      background: white !important;
      color: #f14668;
      height: 14px;
      margin-left: 2px;
      text-transform: uppercase;
    }
    &.small {
      padding: 0 3px !important;
      border-radius: 6px;
      line-height: 100%;
      height: 18px;
      font-size: $size-8;
      margin: 1px 2px !important;
    }
    &.music-categorisation {
      border: 1px solid $bluegrey6;
      color: $bluegrey3;
      background-color: $white !important;
    }
    &.new {
      margin-left: 4px;
      font-size: 8px;
      letter-spacing: 0.07em;
      padding: 4px;
      border: 1px solid $green !important;
      background: white !important;
      color: $green;
      height: 14px;
      text-transform: uppercase;
    }
  }
</style>
